import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Activity, Users, FileText, MessageSquare, Car, Shield, AlertTriangle,
  Clock, TrendingUp, Eye, Globe, Bell, CheckCircle, XCircle, RefreshCw,
  Lock, Zap, BarChart3, ArrowUp, ArrowDown, MapPin
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, CartesianGrid
} from 'recharts';

const STATUS_COLORS = { healthy: '#22c55e', degraded: '#f59e0b', critical: '#ef4444' };
const PIE_COLORS = ['#FF3B30', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#6366f1', '#14b8a6'];

function StatCard({ icon: Icon, label, value, sub, color = '#FF3B30' }) {
  return (
    <Card className="bg-[#141414] border-[#262626] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{label}</p>
          <p className="text-white text-2xl font-bold">{value}</p>
          {sub && <p className="text-gray-500 text-xs mt-1">{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </Card>
  );
}

function SectionHeader({ icon: Icon, title, color = '#FF3B30' }) {
  return (
    <div className="flex items-center gap-3 mb-4 mt-8">
      <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <h2 className="font-heading font-bold text-xl text-white">{title}</h2>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [alertsHistory, setAlertsHistory] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchDashboard();
    fetchAccessibilityAudit();
    fetchAlertsHistory();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/admin/dashboard');
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchAccessibilityAudit = async () => {
    setAuditLoading(true);
    try {
      const res = await api.get('/admin/accessibility-audit');
      setAudit(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchAlertsHistory = async () => {
    try {
      const res = await api.get('/admin/alerts/history');
      setAlertsHistory(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
    fetchAccessibilityAudit();
    fetchAlertsHistory();
  };

  const handleTestAlert = async () => {
    setTestingAlert(true);
    try {
      const res = await api.post('/admin/test-alert');
      alert(res.data.message);
      fetchAlertsHistory();
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setTestingAlert(false);
    }
  };

  const [seedingTracks, setSeedingTracks] = useState(false);
  const handleSeedTracks = async (wipe = false) => {
    const confirmMsg = wipe
      ? 'This will WIPE the current Track Atlas and reseed with the default 100 tracks. Continue?'
      : 'Upsert the default 100-track Track Atlas? Existing tracks with matching ids will be updated.';
    if (!window.confirm(confirmMsg)) return;
    setSeedingTracks(true);
    try {
      const res = await api.post(`/admin/tracks/seed-defaults?wipe=${wipe ? 'true' : 'false'}`);
      alert(`Done. Inserted ${res.data.inserted}, updated ${res.data.updated}. Total in DB: ${res.data.total_in_db}.`);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setSeedingTracks(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data) return null;

  const { health, user_analytics, content, security, last_scrape } = data;

  // Prepare chart data
  const trafficData = Object.entries(user_analytics.traffic_sources || {}).map(([name, value]) => ({ name, value }));
  const pageData = Object.entries(user_analytics.page_views || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, views]) => ({ name: name.replace(/\//g, '/').substring(0, 20), views }));

  const errorRateData = [
    { name: '2xx', value: health.success_requests, fill: '#22c55e' },
    { name: '4xx', value: Math.round(health.total_requests * health.error_rate_4xx / 100), fill: '#f59e0b' },
    { name: '5xx', value: Math.round(health.total_requests * health.error_rate_5xx / 100), fill: '#ef4444' },
  ].filter(d => d.value > 0);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Admin</p>
              <h1 className="font-heading font-bold text-3xl text-white">Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handleTestAlert}
                disabled={testingAlert}
                variant="outline"
                className="border-[#262626] text-white"
                data-testid="test-alert-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                {testingAlert ? 'Sending...' : 'Test Alert'}
              </Button>
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-[#FF3B30] hover:bg-[#E6352B]"
                data-testid="refresh-dashboard-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">

        {/* ========== HEALTH MONITOR ========== */}
        <SectionHeader icon={Activity} title="System Health" color="#22c55e" />
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-[#141414] border-[#262626] p-5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">Status</p>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full animate-pulse" style={{ backgroundColor: STATUS_COLORS[health.status] }}></div>
              <span className="text-white font-bold text-lg capitalize">{health.status}</span>
            </div>
          </Card>
          <StatCard icon={Clock} label="Uptime" value={health.uptime_formatted} color="#22c55e" />
          <StatCard icon={Zap} label="Total Requests" value={health.total_requests.toLocaleString()} color="#3b82f6" />
          <StatCard icon={AlertTriangle} label="Error Rate (5xx)" value={`${health.error_rate_5xx}%`} color={health.error_rate_5xx > 5 ? '#ef4444' : '#22c55e'} />
        </div>

        {/* Error Rate Chart + Recent Errors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="bg-[#141414] border-[#262626] p-5">
            <h3 className="text-white font-medium text-sm mb-4">Response Status Distribution</h3>
            {errorRateData.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={errorRateData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                    {errorRateData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#1F1F1F', border: '1px solid #262626', borderRadius: '8px', color: '#fff' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">No request data yet</p>
            )}
            <div className="flex justify-center gap-4 mt-2">
              {errorRateData.map((d, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.fill }}></div>
                  <span className="text-gray-400">{d.name}: {d.value}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="bg-[#141414] border-[#262626] p-5">
            <h3 className="text-white font-medium text-sm mb-4">Recent Errors</h3>
            <div className="space-y-2 max-h-[240px] overflow-y-auto">
              {health.recent_errors.length > 0 ? health.recent_errors.map((err, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#262626] last:border-0">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-mono px-2 py-0.5 rounded ${err.status >= 500 ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {err.status}
                    </span>
                    <span className="text-gray-300 text-xs">{err.method} {err.path}</span>
                  </div>
                  <span className="text-gray-500 text-xs">{err.duration}s</span>
                </div>
              )) : (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
                  <span className="text-sm">No recent errors</span>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* ========== USER ANALYTICS ========== */}
        <SectionHeader icon={Users} title="User Analytics" color="#3b82f6" />
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard icon={Users} label="Total Users" value={user_analytics.total_users} color="#3b82f6" />
          <StatCard icon={TrendingUp} label="Today" value={`+${user_analytics.new_today}`} color="#22c55e" />
          <StatCard icon={TrendingUp} label="This Week" value={`+${user_analytics.new_this_week}`} color="#22c55e" />
          <StatCard icon={TrendingUp} label="This Month" value={`+${user_analytics.new_this_month}`} color="#22c55e" />
          <StatCard icon={Lock} label="Blocked" value={user_analytics.blocked_users} color="#ef4444" />
        </div>

        {/* Signup Chart + Traffic Sources */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="bg-[#141414] border-[#262626] p-5">
            <h3 className="text-white font-medium text-sm mb-4">Daily Signups (14 days)</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={user_analytics.daily_signups}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis tick={{ fill: '#888', fontSize: 11 }} allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: '#1F1F1F', border: '1px solid #262626', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="signups" fill="#FF3B30" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card className="bg-[#141414] border-[#262626] p-5">
            <h3 className="text-white font-medium text-sm mb-4">Traffic Sources</h3>
            {trafficData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={trafficData} cx="50%" cy="50%" outerRadius={60} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {trafficData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1F1F1F', border: '1px solid #262626', borderRadius: '8px', color: '#fff' }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-3 mt-2 justify-center">
                  {trafficData.map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}></div>
                      <span className="text-gray-400">{d.name}: {d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">No traffic data yet. Analytics will populate as users visit.</p>
            )}
          </Card>
        </div>

        {/* Page Views */}
        {pageData.length > 0 && (
          <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
            <h3 className="text-white font-medium text-sm mb-4">Top Pages</h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={pageData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis type="number" tick={{ fill: '#888', fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fill: '#888', fontSize: 11 }} width={120} />
                <Tooltip contentStyle={{ backgroundColor: '#1F1F1F', border: '1px solid #262626', borderRadius: '8px', color: '#fff' }} />
                <Bar dataKey="views" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* ========== CONTENT STATS ========== */}
        <SectionHeader icon={FileText} title="Content" color="#f59e0b" />
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <StatCard icon={FileText} label="News Articles" value={content.news_articles} color="#f59e0b" />
          <StatCard icon={MessageSquare} label="Forum Threads" value={content.forum_threads} color="#f59e0b" />
          <StatCard icon={MessageSquare} label="Forum Posts" value={content.forum_posts} color="#f59e0b" />
          <StatCard icon={Car} label="Car Listings" value={content.car_listings} color="#f59e0b" />
          <StatCard icon={Zap} label="Forum Reactions" value={content.forum_reactions} color="#f59e0b" />
          <StatCard icon={TrendingUp} label="Article Likes" value={content.article_reactions} color="#f59e0b" />
        </div>

        {/* ========== SECURITY ========== */}
        <SectionHeader icon={Shield} title="Security" color="#ef4444" />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatCard icon={AlertTriangle} label="Failed Login Attempts" value={security.failed_login_attempts} color="#ef4444" />
          <StatCard icon={Lock} label="Blocked Users" value={security.blocked_users} color="#ef4444" />
          <Card className="bg-[#141414] border-[#262626] p-5">
            <p className="text-gray-500 text-xs uppercase tracking-wider mb-2">SSL Status</p>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-white font-bold">Active</span>
            </div>
          </Card>
        </div>

        {security.recent_failed_logins.length > 0 && (
          <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
            <h3 className="text-white font-medium text-sm mb-4">Recent Failed Login Attempts</h3>
            <div className="space-y-2">
              {security.recent_failed_logins.map((attempt, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#262626] last:border-0">
                  <span className="text-gray-300 text-sm font-mono">{attempt.identifier}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-red-400 text-xs">{attempt.count} attempts</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* ========== SCRAPER STATUS ========== */}
        <SectionHeader icon={Globe} title="News Scraper" color="#8b5cf6" />
        
        {last_scrape ? (
          <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Last Run</p>
                <p className="text-white text-sm">{new Date(last_scrape.timestamp).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Articles Found</p>
                <p className="text-white text-lg font-bold">{last_scrape.total_found}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">New Saved</p>
                <p className="text-white text-lg font-bold">{last_scrape.new_saved}</p>
              </div>
              <div>
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Images Stored</p>
                <p className="text-white text-lg font-bold">{last_scrape.images_stored || 0}</p>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
            <p className="text-gray-500">No scrape data available yet.</p>
          </Card>
        )}

        {/* ========== ACCESSIBILITY ========== */}
        <div className="flex items-center justify-between mt-8 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: '#14b8a620' }}>
              <Eye className="w-4 h-4" style={{ color: '#14b8a6' }} />
            </div>
            <h2 className="font-heading font-bold text-xl text-white">Accessibility (WCAG 2.1)</h2>
            {audit && (
              <span
                className="tag px-2 py-0.5 rounded text-xs font-bold"
                style={{
                  backgroundColor:
                    audit.score >= 85 ? '#22c55e20' : audit.score >= 60 ? '#f59e0b20' : '#ef444420',
                  color: audit.score >= 85 ? '#22c55e' : audit.score >= 60 ? '#f59e0b' : '#ef4444',
                }}
                data-testid="a11y-score"
              >
                Score: {audit.score}%
              </span>
            )}
          </div>
          <Button
            onClick={fetchAccessibilityAudit}
            disabled={auditLoading}
            variant="outline"
            size="sm"
            className="border-[#262626] text-white"
            data-testid="run-a11y-audit-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${auditLoading ? 'animate-spin' : ''}`} />
            {auditLoading ? 'Scanning…' : 'Run Audit'}
          </Button>
        </div>

        <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
          {audit ? (
            <>
              <div className="flex items-center gap-4 mb-5 text-xs text-gray-400">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-green-500" />{audit.summary.passed} passed</span>
                <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />{audit.summary.warned} warnings</span>
                <span className="flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5 text-red-500" />{audit.summary.failed} failed</span>
                <span className="ml-auto text-gray-500">
                  Scanned {audit.scanned.jsx_files} components · {audit.scanned.images_found} images · {audit.scanned.inputs_found} inputs
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {audit.checks.map((check) => {
                  const styles = {
                    pass: { icon: CheckCircle, color: 'text-green-500', border: 'border-[#262626]' },
                    warn: { icon: AlertTriangle, color: 'text-yellow-500', border: 'border-yellow-500/30' },
                    fail: { icon: XCircle, color: 'text-red-500', border: 'border-red-500/40' },
                  }[check.status];
                  const Icon = styles.icon;
                  return (
                    <div
                      key={check.id}
                      className={`p-4 bg-[#1F1F1F] rounded border ${styles.border}`}
                      data-testid={`a11y-check-${check.id}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Icon className={`w-4 h-4 ${styles.color}`} />
                        <span className="text-white font-medium text-sm">{check.label}</span>
                        <span className="ml-auto text-gray-500 text-[10px] uppercase tracking-wider">{check.wcag}</span>
                      </div>
                      <p className="text-gray-400 text-xs mb-1">{check.metric}</p>
                      <p className="text-gray-500 text-xs">{check.detail}</p>
                    </div>
                  );
                })}
              </div>
              {audit.run_at && (
                <p className="text-gray-600 text-xs mt-4">
                  Last scanned: {new Date(audit.run_at).toLocaleString()}
                </p>
              )}
            </>
          ) : (
            <p className="text-gray-500 text-sm text-center py-6">
              {auditLoading ? 'Running accessibility audit…' : 'Click "Run Audit" to scan the frontend for WCAG 2.1 issues.'}
            </p>
          )}
        </Card>

        {/* ========== TRACK ATLAS ========== */}
        <SectionHeader icon={MapPin} title="Track Atlas" color="#22c55e" />
        <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p className="text-white font-medium text-sm">Crown Vic Track Intelligence Map</p>
              <p className="text-gray-500 text-xs mt-1 max-w-md">
                Seed the national 100-track dataset on this deployment. Production uses a separate DB
                from preview — run this once per deploy to populate crownvicnation.com.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => handleSeedTracks(false)}
                disabled={seedingTracks}
                className="bg-[#22c55e] hover:bg-[#16a34a] text-white"
                data-testid="seed-tracks-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${seedingTracks ? 'animate-spin' : ''}`} />
                {seedingTracks ? 'Seeding…' : 'Seed / Refresh Default 100 Tracks'}
              </Button>
              <Button
                onClick={() => handleSeedTracks(true)}
                disabled={seedingTracks}
                variant="outline"
                className="border-[#262626] text-red-400 hover:text-red-300"
                data-testid="wipe-tracks-btn"
              >
                Wipe &amp; Reseed
              </Button>
              <a href="/tracks" className="self-center">
                <Button variant="ghost" className="text-gray-300">
                  <MapPin className="w-4 h-4 mr-2" /> Open Atlas
                </Button>
              </a>
            </div>
          </div>
        </Card>

        {/* ========== EMAIL ALERTS ========== */}
        <SectionHeader icon={Bell} title="Automated Alerts" color="#ec4899" />

        <Card className="bg-[#141414] border-[#262626] p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium text-sm">Alert Rules</h3>
            {alertsHistory?.configured ? (
              <span className="tag bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs">SENDGRID ACTIVE</span>
            ) : (
              <span className="tag bg-yellow-500/20 text-yellow-400 px-2 py-0.5 rounded text-xs">NOT CONFIGURED</span>
            )}
          </div>
          <div className="space-y-2">
            {[
              {
                event: 'Server Error Spike (5xx)',
                detail: alertsHistory
                  ? `≥${alertsHistory.throttle['5xx_spike_threshold']} errors in ${alertsHistory.throttle['5xx_spike_window_minutes']} min · ${alertsHistory.throttle['5xx_cooldown_minutes']} min cooldown`
                  : '—',
              },
              {
                event: 'Brute-force Lockout',
                detail: alertsHistory
                  ? `On 5+ failed logins · ${alertsHistory.throttle.security_cooldown_minutes} min cooldown`
                  : '—',
              },
              { event: 'New User Registration', detail: 'Instant notification to admin' },
            ].map((a, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-[#262626] last:border-0">
                <div className="flex items-center gap-3">
                  <Bell className="w-4 h-4 text-[#FF3B30]" />
                  <div>
                    <span className="text-white text-sm block">{a.event}</span>
                    <span className="text-gray-500 text-xs">{a.detail}</span>
                  </div>
                </div>
                <span className="text-gray-400 text-xs">Email</span>
              </div>
            ))}
          </div>
          {alertsHistory?.notification_email && (
            <div className="mt-4 pt-4 border-t border-[#262626]">
              <p className="text-gray-500 text-xs">Delivering to: <span className="text-gray-300">{alertsHistory.notification_email}</span></p>
            </div>
          )}
        </Card>

        <Card className="bg-[#141414] border-[#262626] p-5 mb-10" data-testid="alert-history-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white font-medium text-sm">Recent Alerts</h3>
            <span className="text-gray-500 text-xs">{alertsHistory?.alerts?.length || 0} in memory</span>
          </div>
          {alertsHistory?.alerts?.length ? (
            <div className="space-y-2">
              {alertsHistory.alerts.slice(0, 10).map((a, i) => (
                <div key={i} className="flex items-start justify-between py-2 border-b border-[#262626] last:border-0">
                  <div>
                    <div className="flex items-center gap-2">
                      <span
                        className="tag px-2 py-0.5 rounded text-[10px] uppercase tracking-wider"
                        style={{
                          backgroundColor: a.kind === 'security' ? '#ef444420' : '#f59e0b20',
                          color: a.kind === 'security' ? '#ef4444' : '#f59e0b',
                        }}
                      >
                        {a.kind === 'security' ? 'Security' : 'Error Spike'}
                      </span>
                      <span className="text-white text-sm">{a.subject}</span>
                    </div>
                    {a.details && (
                      <p className="text-gray-500 text-xs mt-1 font-mono">
                        {Object.entries(a.details).map(([k, v]) => `${k}=${v}`).join(' · ')}
                      </p>
                    )}
                  </div>
                  <span className="text-gray-500 text-xs flex-shrink-0 ml-4">{new Date(a.time).toLocaleTimeString()}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-xs text-center py-4">No alerts fired yet. Click "Test Alert" above to send a sample.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
